<template>
  <ais-range-input
    v-if="isPrice"
    :attribute="attribute"
    class="algolia-facet__range-slider"
  >
    <template #default="{ currentRefinement, range, refine }">
      <div
        v-show="range.min !== range.max"
        class="algolia-facet-mobile"
        :class="{[$class('active')]: open}"
      >
        <button
          class="algolia-facet-mobile__control"
          type="button"
          @click="toggleActiveControl"
        >
          <span
            class="text-label-xs"
            v-text="facetAttribute.label || ''"
          />

          <animated-icon
            :delay-transition="false"
            :focusable="false"
            :transition-on-hover="false"
            transition-type="spin-180"
            :transition-by-prop="open"
          >
            <template #iconOne>
              <icon-plus />
            </template>

            <template #iconTwo>
              <icon-minus />
            </template>
          </animated-icon>
        </button>

        <div class="algolia-facet-mobile__content">
          <algolia-range-slider
            v-if="range.min !== range.max"
            :min="range.min"
            :max="range.max"
            :value="toValue(currentRefinement, range)"
            @algolia-range-updated="(value) => refine({ min: value.min, max: value.max })"
          />
        </div>
      </div>
    </template>
  </ais-range-input>
  <div
    v-else-if="filters.length && !isPrice"
    class="algolia-facet-mobile"
    :class="{[$class('active')]: open}"
  >
    <button
      class="algolia-facet-mobile__control"
      type="button"
      @click="toggleActiveControl"
    >
      <span
        class="text-label-xs"
        v-text="facetAttribute.label || ''"
      />

      <animated-icon
        :delay-transition="false"
        :focusable="false"
        :transition-on-hover="false"
        transition-type="spin-180"
        :transition-by-prop="open"
      >
        <template #iconOne>
          <icon-plus />
        </template>

        <template #iconTwo>
          <icon-minus />
        </template>
      </animated-icon>
    </button>

    <div class="algolia-facet-mobile__content">
      <ul
        class="algolia-facet-mobile__content-inner"
        :class="{['algolia-facet-mobile__content-inner--columns']: showOptionsAsColumns}"
      >
        <li
          v-for="facet of filters"
          :key="facet.value"
        >
          <algolia-facet-value
            v-if="facet.value"
            class="algolia-facet-mobile__value"
            :is-colour="isColour"
            :facet="facet"
            :active="open"
            @click="$emit('choices-updated', facet.value)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Algolia facet mobile (algolia-drawer-mobile)
 * -----------------------------------------------------------------------------
 * Algolia filter facet for mobile accordion content.
 *
 * @param {String} choices - Available choices.
 * @param {Object} facet - Component facet.
 * @param {String} tempChoices - Temporary choices.
 *
 */
import { AisRangeInput } from 'vue-instantsearch/vue3/es'
import AlgoliaFacetValue from '~async/algolia-facet-value/algolia-facet-value'
import AlgoliaRangeSlider from '~async/algolia-range-slider/algolia-range-slider'
import AnimatedIcon from '~global/animated-icon/animated-icon'

import IconMinus from '~icons/misc/minus.svg'
import IconPlus from '~icons/misc/plus.svg'

export default {
  name: 'AlgoliaFacetMobile',

  components: {
    AnimatedIcon,
    AlgoliaFacetValue,
    AlgoliaRangeSlider,
    IconPlus,
    IconMinus,
    AisRangeInput,
  },

  props: {
    facetAttribute: {
      type: Object,
      default: () => ({}),
    },
    facets: {
      type: Array,
      default: () => [],
    },
    open: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'string',
    'facet-toggled': () => true,
  },

  computed: {

    /**
     * Is this a facet from Price refinement list.
     */
    isPrice() {
      return (this.facetAttribute?.value?.toLowerCase() || '').includes('price')
    },

    /**
     * Is this a facet from Colour refinement list.
     */
    isColour() {
      return (this.facetAttribute?.value?.toLowerCase() || '').includes('colour')
    },

    /**
     * Is this a facet from Price refinement list.
     */
    isSize() {
      return (this.facetAttribute?.value?.toLowerCase() || '').includes('size')
    },

    attribute() {
      const marketHandle = this.$variable('store.marketHandle')
      return `price.${marketHandle}.amount`
    },

    filters() {
      const preparedFiltered = []

      this.facets.forEach(facet => {
        if (this.isPrice) {
          return
        }

        preparedFiltered.push(facet)
      })

      return preparedFiltered
    },

    /**
     * Splits content into columsn for colours and sizes.
     * @returns {Boolean}
     */
    showOptionsAsColumns() {
      return this.isSize || this.isColour
    },
  },

  methods: {

    /**
     * Returns an array ot 2 number the first is the minimum applied refinement
     * and the second is the maximum one. From Algolia docs:
     * https://www.algolia.com/doc/api-reference/widgets/range-slider/vue/
     *
     * @param {Object} value - current Algolia range input refinement
     * @param {Object} range - The minimum and maximum available values.
     */
    toValue(value, range) {
      return [
        typeof value.min === "number" ? value.min : range.min,
        typeof value.max === "number" ? value.max : range.max,
      ]
    },

    /**
     * Toggle active class on facet click.
     * @param {Event} event - Click event.
     */
    toggleActiveControl() {
      if (this.open) {
        this.$emit('facet-toggled', null)
        return
      }

      this.$emit('facet-toggled', this.facetAttribute.label)
    },
  },
}
</script>

<style lang="scss">
@import './algolia-facet-mobile';
</style>
